import {XIcon} from "assets/icons";
import Dropdown, {DropdownRef} from "components/Dropdown";
import Tooltip from "components/Tooltip";
import {ChevronDown, CircleDot, MapPin, Monitor, SlidersHorizontal} from "lucide-react";
import {FC, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";

const availableEmployment = ["Full Time", "Part Time", "Freelance Contract"];
const availableSoftware = ["QuickBooks Online", "QuickBooks Desktop", "Xero", "FreshBooks", "MS Excel", "SAP"];

interface Props {
  noEmployment?: boolean;
}

const FilterCheckbox: FC<{checked: boolean}> = ({checked}) => (
  <span
    className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors"
    style={
      checked
        ? {backgroundColor: "#F07340", borderColor: "#F07340"}
        : {backgroundColor: "white", borderColor: "#D1CFC9"}
    }
  >
    {checked && (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 5L4.2 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

const SectionHeader: FC<{children: React.ReactNode}> = ({children}) => (
  <p className="text-[10px] font-semibold tracking-widest uppercase mb-2" style={{color: "#A09A93"}}>
    {children}
  </p>
);

const ManualFilters: FC<Props> = ({noEmployment}) => {
  const dropdownRef = useRef<DropdownRef>(null);
  const [sp, setSp] = useSearchParams();
  const [employment, setEmployment] = useState<string[]>(sp.get("employment")?.split(":") || []);
  const [software, setSoftware] = useState<string[]>(sp.get("software")?.split(":") || []);
  const [zip, setZip] = useState(sp.get("zip") || "");
  const [radius, setRadius] = useState(sp.get("radius")?.split(":").at(-1) || "");

  // Keep local draft in sync whenever URL params change (e.g. after onboarding sets them)
  useEffect(() => {
    setEmployment(sp.get("employment")?.split(":") || []);
    setSoftware(sp.get("software")?.split(":") || []);
    setZip(sp.get("zip") || "");
    setRadius(sp.get("radius")?.split(":").at(-1) || "");
  }, [sp]);

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((e) => e !== value) : [...list, value];

  const apply = () => {
    setSp((old) => {
      if (employment.length) old.set("employment", employment.join(":"));
      else old.delete("employment");

      if (software.length) old.set("software", software.join(":"));
      else old.delete("software");

      if (zip) {
        old.set("zip", zip);
        if (radius) old.set("radius", radius);
        else old.delete("radius");
      } else {
        old.delete("zip");
        old.delete("radius");
      }

      return old;
    });
  };

  const clearAndApply = () => {
    setEmployment([]);
    setSoftware([]);
    setZip("");
    setRadius("");
    setSp((old) => {
      old.delete("employment");
      old.delete("software");
      old.delete("zip");
      old.delete("radius");
      return old;
    });
  };

  const hasFilters = employment.length > 0 || software.length > 0 || !!zip || !!radius;

  const triggerContent = (open: boolean) => (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-brand-muted">
      <SlidersHorizontal className="w-3.5 h-3.5" />
      Manual Filters
      <ChevronDown className="w-3.5 h-3.5" />
    </span>
  );

  const triggerClassName = (open: boolean) =>
    [
      "flex items-center gap-1.5 px-3 py-2 rounded-xl border border-brand-border hover:shadow-sm transition-all",
      open ? "bg-[#FEF3EA]" : "bg-white",
    ].join(" ");

  return (
    <div className="flex items-center gap-1">
      <Dropdown
        ref={dropdownRef}
        value="Manual Filters"
        dontCloseOnSelect
        triggerContent={triggerContent}
        triggerClassName={triggerClassName}
      >
        <div
          className="bg-white rounded-xl border border-brand-border shadow-lg mt-2 z-[15]
            max-h-[60vh] overflow-y-auto min-w-[220px]"
        >
          {/* Location */}
          <div className="px-4 pt-4 pb-3">
            <SectionHeader>Location</SectionHeader>
            <div className="flex flex-col gap-2">
              <div className="relative">
                <MapPin
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                  style={{color: "#A09A93"}}
                />
                <input
                  value={zip}
                  onChange={(e) => setZip(e.currentTarget.value)}
                  placeholder="ZIP Code"
                  className="w-full pl-7 pr-3 py-2 rounded-lg border text-xs outline-none transition-colors"
                  style={{borderColor: "#E8E4DF", backgroundColor: "#F7F5F2", color: "#282D4D"}}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#F07340")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#E8E4DF")}
                />
              </div>
              <div className="relative">
                <CircleDot
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                  style={{color: "#A09A93"}}
                />
                <input
                  value={radius}
                  onChange={(e) => setRadius(e.currentTarget.value)}
                  placeholder="Miles radius (e.g. 25)"
                  className="w-full pl-7 pr-3 py-2 rounded-lg border text-xs outline-none transition-colors"
                  style={{borderColor: "#E8E4DF", backgroundColor: "#F7F5F2", color: "#282D4D"}}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#F07340")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#E8E4DF")}
                />
              </div>
            </div>
          </div>

          {!noEmployment && (
            <>
              <div className="mx-4 h-px bg-brand-border" />
              <div className="px-4 pt-3 pb-3">
                <SectionHeader>Availability</SectionHeader>
                <div className="flex flex-col gap-0.5">
                  {availableEmployment.map((et) => (
                    <button
                      key={et}
                      onClick={() => setEmployment((old) => toggle(old, et))}
                      className="flex items-center gap-2.5 py-1.5 px-1 rounded-md hover:bg-brand-cream transition-colors text-left w-full"
                    >
                      <FilterCheckbox checked={employment.includes(et)} />
                      <span className="text-xs" style={{color: "#3D3530"}}>
                        {et}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="mx-4 h-px bg-brand-border" />
          <div className="px-4 pt-3 pb-3">
            <SectionHeader>
              <Monitor className="inline w-3 h-3 mr-1 mb-0.5" />
              Software
            </SectionHeader>
            <div className="flex flex-col gap-0.5">
              {availableSoftware.map((sw) => (
                <button
                  key={sw}
                  onClick={() => setSoftware((old) => toggle(old, sw))}
                  className="flex items-center gap-2.5 py-1.5 px-1 rounded-md hover:bg-brand-cream transition-colors text-left w-full"
                >
                  <FilterCheckbox checked={software.includes(sw)} />
                  <span className="text-xs" style={{color: "#3D3530"}}>
                    {sw}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 pb-4 pt-2 flex gap-2 justify-end border-t border-brand-border">
            <button
              onClick={() => dropdownRef.current?.setOpen(false)}
              className="text-xs px-4 py-2 rounded-lg border border-brand-border bg-white hover:shadow-sm transition-all"
              style={{color: "#8490AA"}}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                apply();
                dropdownRef.current?.setOpen(false);
              }}
              className="text-xs text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              style={{backgroundColor: "#F07340"}}
            >
              Apply
            </button>
          </div>
        </div>
      </Dropdown>

      {hasFilters && (
        <Tooltip text="Clear Filters" position="top">
          <button onClick={clearAndApply} className="btn-secondary text-text-secondary !p-2">
            <XIcon />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default ManualFilters;
